import { ComponentType } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  Signal,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HighchartsChartData } from '@api-response-types/highcharts-chart-data.response';
import { Resolution } from '@api-types/enums/resolution.enum';
import { getFactorColumn } from '@api-types/helper/helper';
import { FilterValue } from '@api-types/types/filter-value.type';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { HighchartsChartComponent } from 'highcharts-angular';
import { WidgetChangeDetectionObject } from '@api-response-types/forecast-series.response';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { Filter } from '@api-types/types/filter.type';
import { Timespan } from '@api-types/types/timespan.type';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H3Component,
  PmButtonComponent,
  PmTooltipDirective,
} from '@libs/ui';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { selectAvailableFiltersAsRecord } from 'demand-forecasting/store/filter/filter.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  FORECAST_PLOT_BAND,
  FORECAST_PLOT_LINE,
} from 'demand-forecasting/types/forecast-chart/forecast-chart-options';
import { HighchartsOptions } from 'demand-forecasting/types/forecast-chart/highcharts-options';
import { isArray } from 'lodash-es';
import { LucideScan } from '@lucide/angular';
import { DefaultSelectionSingleForecastsWidget } from '../../types/types';
import { calculatePlotbandStartDateAndSeriesMaxDate } from '../../utils';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pm-single-forecasts-widget',
  templateUrl: './single-forecasts-widget.component.html',
  imports: [
    TranslateModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    HighchartsChartComponent,
    ReactiveFormsModule,
    FormsModule,
    WidgetHeaderDirective,
    WidgetTitleDirective,
    WidgetBaseComponent,
    WidgetContentDirective,
    WidgetActionsDirective,
    PmTooltipDirective,
    H3Component,
    PmButtonComponent,
    FormFieldComponent,
    FormInputSelectComponent,
  ],
})
export class SingleForecastsWidgetComponent extends WidgetBaseComponent<SingleForecastsWidgetComponent> {
  @Input({
    required: false,
  })
  targetUnit?: string;

  @Input({
    required: false,
  })
  resolution?: Resolution;

  @Input({
    required: false,
  })
  filterColumn?: string;

  @Input({
    required: false,
  })
  filterOptions?: FilterValue[];

  chartData: HighchartsChartData;

  private readonly destroyRef = inject(DestroyRef);

  protected readonly scanIcon = LucideScan;

  private readonly store = inject(Store);
  private readonly insightsHttpClient: InsightsHttpClient =
    inject(InsightsHttpClient);
  protected readonly MAX_NUMBER_OF_SELECTIONS = 5;
  invalidDataset = false;
  isError = false;

  protected chartOptions = signal<HighchartsOptions>(
    new ChartOptionsBuilder().build(),
  );

  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  // selected Filter with all possible values
  private readonly forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );
  private readonly forecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );

  protected availableFilters = this.store.selectSignal(
    selectAvailableFiltersAsRecord,
  );
  protected selectedFilter = signal<Filter>(
    this.findFilterForColumn(this.filterColumn) ?? this.availableFilters()[0],
  );

  protected selectedFilterColumn = computed(() => {
    return this.selectedFilter().column;
  });

  protected selectedValuesFormControl = new FormControl<FilterValue[]>(
    this.getDefaultSelectionsForFilterValues(),
    { nonNullable: true },
  );
  filteredValuesForSelectedFilter = signal<FilterValue[]>([]);

  private plotBandStartDate = 0;
  private seriesMaxDate = 0;
  private timespan: Timespan | undefined;

  previousSingleForecastsChangeDetectionObject: WidgetChangeDetectionObject;

  filterOptionsForSelect: Signal<NgxVirtualSelectFieldOptionModel<Filter>[]> =
    computed(() =>
      this.availableFilters().map((f) => ({
        label: f.column,
        value: f,
        disabled: false,
      })),
    );

  filterValuesForSelect: Signal<
    NgxVirtualSelectFieldOptionModel<FilterValue>[]
  > = computed(() =>
    this.filteredValuesForSelectedFilter().map((v) => ({
      value: v,
      label: String(v),
      disabled: false,
    })),
  );

  private readonly forecastHttpClient = inject(ForecastsHttpClient);
  public data: DefaultSelectionSingleForecastsWidget = inject(MAT_DIALOG_DATA, {
    optional: true,
  });

  constructor() {
    super();
    if (this.data) {
      this.targetUnit = this.data.targetUnit;
      this.filterColumn = this.data.filterColumn;
      this.filterOptions = this.data.filterOptions;
    }

    effect(() => {
      const forecastSeries = this.forecastSeries();
      const forecastVersion = this.forecastVersion();
      const availableFilters = this.availableFilters();

      if (
        !forecastSeries ||
        !forecastVersion ||
        forecastVersion.status !== ForecastVersionStatus.FINISHED ||
        !availableFilters ||
        availableFilters.length === 0
      ) {
        return;
      }

      const newSeriesChangeDetectionObject: WidgetChangeDetectionObject = {
        _resultSetStatus: forecastSeries._resultSetStatus,
        configurationId: forecastSeries.configurationId,
        resultSetId: forecastVersion.id, // TODO forecastSeries.resultSetId seems to never get updated -> Fix via https://pacemaker-ai.atlassian.net/browse/PM-1674
        yAxis: forecastSeries.yAxis,
      };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousSingleForecastsChangeDetectionObject ?? {})
      ) {
        return;
      }
      // Update previous values
      this.previousSingleForecastsChangeDetectionObject =
        newSeriesChangeDetectionObject;

      if (availableFilters.length > 0 && forecastVersion) {
        if (!this.resolution) {
          this.resolution = Resolution.Monthly;
        }
        if (this.timespan) {
          const { plotBandStartDate, seriesMaxDate } =
            calculatePlotbandStartDateAndSeriesMaxDate(
              this.timespan,
              this.resolution,
            );
          this.updatePlotDates(plotBandStartDate, seriesMaxDate);
        } else {
          this.getTimespanAndAdjustPlotDates(forecastVersion.forecastId);
        }
        this.setInitialFilterAndValues();
        this.fetchData(forecastVersion.id);
      } else {
        this.isLoading = false;
        this.invalidDataset = true;
        this.selectedValuesFormControl.disable();
      }
    });

    this.selectedValuesFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onFilterValueChange();
      });
  }

  private getTimespanAndAdjustPlotDates(forecastId: string) {
    this.forecastHttpClient.getTimespan(forecastId).subscribe({
      next: (timespan) => {
        this.timespan = timespan;
        const { plotBandStartDate, seriesMaxDate } =
          calculatePlotbandStartDateAndSeriesMaxDate(
            this.timespan,
            this.resolution ?? Resolution.Monthly,
          );
        this.updatePlotDates(plotBandStartDate, seriesMaxDate);
      },
      error: (err) => {
        console.error('Error fetching timespan:', err);
      },
    });
  }

  private updatePlotDates(plotbandStartDate: number, seriesMaxDate: number) {
    this.plotBandStartDate = plotbandStartDate;
    this.seriesMaxDate = seriesMaxDate;
  }

  private setInitialFilterAndValues() {
    const selected =
      this.availableFilters().find(
        (filter) => filter.column === this.filterColumn,
      ) ?? this.availableFilters()[0];

    this.selectedFilter.set(selected);
    this.filteredValuesForSelectedFilter.set(selected.values);

    this.selectedValuesFormControl.setValue(
      this.filterOptions ?? this.getDefaultSelectionsForFilterValues(),
    );
  }

  setChartOptions(data: { [p: string]: [number, number | null][] }) {
    const chartOptions = new ChartOptionsBuilder()
      .withPlotOptions({
        spline: {
          allowPointSelect: false,
          marker: {
            enabled: false,
          },
        },
      })
      .withXAxis({
        type: 'datetime',
        title: {
          text: this.forecastSeries()?.xAxis || 'date',
        },
        plotBands: [
          FORECAST_PLOT_BAND(this.plotBandStartDate, this.seriesMaxDate),
        ],
        plotLines: [FORECAST_PLOT_LINE(this.plotBandStartDate)],
      })
      .withYAxis({
        title: {
          text: this.targetUnit || this.forecastSeries()?.yAxis || 'value',
        },
        min: 0,
      })
      .withSeries(
        Object.values(data).map((series, idx) => ({
          type: 'spline',
          data: series,
          name: Object.keys(data)[idx],
          marker: { enabled: false },
        })),
      )
      .build();

    this.chartOptions.set(chartOptions);
  }

  fetchData(forecastId: string): void {
    const selectedFilter = this.selectedFilter();
    const filterName = selectedFilter?.column ?? '';
    const config = this.configuration();
    if (!config) {
      console.error(
        'config is not defined or - aborting fetchData in single forecasts widget',
      );
      return;
    }
    const factorColumn = getFactorColumn(config, this.targetUnit);

    const resolution = this.resolution ?? config.startForecastConfig.resolution;

    this.isLoading = true;
    this.isError = false;
    this.insightsHttpClient
      .getSingleForecastsData(
        forecastId,
        filterName,
        this.selectedValuesFormControl.getRawValue(),
        resolution,
        factorColumn,
      )
      .subscribe(
        (data) => {
          this.chartData = data.values;
          this.setChartOptions(data.values);
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          this.isError = true;
        },
      );
  }

  onFilterChange(selectedColumn: string): void {
    const newSelection = this.findFilterForColumn(selectedColumn);

    if (
      !newSelection ||
      selectedColumn === this.selectedFilterColumn() ||
      isArray(newSelection)
    )
      return;

    const forecastVersion = this.forecastVersion();
    if (!forecastVersion) {
      console.warn('forecastVersion is not defined');
      return;
    }

    this.setFilterAndSelectedValues(newSelection);
    this.fetchData(forecastVersion.id);
  }

  private findFilterForColumn(filterColumnName?: string): Filter | undefined {
    if (!filterColumnName) return undefined;

    return this.availableFilters().find(
      (filter) => filter.column === filterColumnName,
    );
  }

  setFilterAndSelectedValues(filter: Filter) {
    this.selectedFilter.set(filter);
    this.filteredValuesForSelectedFilter.set(filter?.values || []);
    this.selectedValuesFormControl.setValue(
      this.getDefaultSelectionsForFilterValues(),
    );
  }

  getDefaultSelectionsForFilterValues() {
    const selectedFilter = this.selectedFilter();
    if (
      selectedFilter &&
      selectedFilter.values.length > this.MAX_NUMBER_OF_SELECTIONS
    ) {
      return selectedFilter.values.slice(0, this.MAX_NUMBER_OF_SELECTIONS);
    }
    if (selectedFilter) {
      return selectedFilter.values;
    }
    return [];
  }

  onFilterValueChange(): void {
    const forecastVersion = this.forecastVersion();
    if (!forecastVersion) {
      console.warn('forecastVersion is not defined');
      return;
    }
    this.fetchData(forecastVersion.id);
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<SingleForecastsWidgetComponent>,
    filterSelection?: DefaultSelectionSingleForecastsWidget,
  ): MatDialogRef<SingleForecastsWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);

    dialog?.afterClosed().subscribe(() => {
      this.filterColumn = dialog.componentInstance.selectedFilter().column;
      this.filterOptions =
        dialog.componentInstance.selectedValuesFormControl.getRawValue();

      const availableFilters = this.availableFilters();
      const forecastVersion = this.forecastVersion();
      if (availableFilters.length > 0 && forecastVersion) {
        this.setInitialFilterAndValues();
        this.fetchData(forecastVersion.id);
      } else {
        this.isLoading = false;
        this.invalidDataset = true;
        this.selectedValuesFormControl.disable();
      }
    });
    return null;
  }

  get SingleForecastsWidgetComponent(): typeof SingleForecastsWidgetComponent {
    return SingleForecastsWidgetComponent;
  }
}
